import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Mail, Plus, Edit, Trash2, Save, Eye, FileText, Send, Users, Receipt } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const TEMPLATE_TYPES = [
  { value: 'payment_request', label: 'Payment Request', icon: Receipt, color: 'bg-green-500', description: 'Request payment from clients' },
  { value: 'quotation', label: 'Quotation', icon: FileText, color: 'bg-blue-500', description: 'Send quotation to clients' },
  { value: 'invoice', label: 'Invoice', icon: Receipt, color: 'bg-purple-500', description: 'Send invoice to clients' },
  { value: 'vendor_payment', label: 'Vendor Payment', icon: Users, color: 'bg-orange-500', description: 'Payment notification to vendors' },
  { value: 'project_update', label: 'Project Update', icon: FileText, color: 'bg-teal-500', description: 'Project status update to clients' },
];

// Default template content
const DEFAULT_TEMPLATES = {
  payment_request: {
    subject: 'Payment Request - {{project_name}} | {{company_name}}',
    body: `Dear {{client_name}},

Greetings from {{company_name}}!

We hope this email finds you well. This is a gentle reminder regarding the pending payment for your project "{{project_name}}".

Payment Details:
- Project: {{project_name}}
- Amount Due: ₹{{amount}}
- Payment Stage: {{payment_stage}}

Please find the payment request attached for your reference.

Bank Details:
{{bank_details}}

If you have already made the payment, please share the payment confirmation for our records.

For any queries, please feel free to reach out to us.

Thank you for your continued trust in us.

Best regards,
{{sender_name}}
{{company_name}}
{{company_phone}}
{{company_email}}`
  },
  quotation: {
    subject: 'Quotation - {{project_name}} | {{company_name}}',
    body: `Dear {{client_name}},

Thank you for giving us the opportunity to provide you with a quotation for {{project_name}}.

Please find the detailed quotation attached to this email.

Quotation Summary:
- Project: {{project_name}}
- Total Amount: ₹{{total_amount}}
- Validity: {{validity_date}}

We have carefully considered your requirements and have provided a comprehensive solution that meets your needs while ensuring quality and value.

Please review the quotation at your convenience. We would be happy to discuss any modifications or clarifications you may need.

Looking forward to your positive response.

Best regards,
{{sender_name}}
{{company_name}}`
  },
  invoice: {
    subject: 'Invoice #{{invoice_number}} - {{project_name}} | {{company_name}}',
    body: `Dear {{client_name}},

Please find attached the invoice for {{project_name}}.

Invoice Details:
- Invoice No: {{invoice_number}}
- Amount: ₹{{amount}}
- Due Date: {{due_date}}

Kindly process the payment at the earliest.

Thank you for your business.

Best regards,
{{sender_name}}
{{company_name}}`
  },
  vendor_payment: {
    subject: 'Payment Processed - {{company_name}}',
    body: `Dear {{vendor_name}},

This is to confirm that a payment of ₹{{amount}} has been processed to your account for {{project_name}}.

Payment Details:
- Amount: ₹{{amount}}
- Payment Mode: {{payment_mode}}
- Project: {{project_name}}
- Reference: {{reference_id}}

Please confirm receipt of this payment.

Best regards,
{{sender_name}}
{{company_name}}`
  },
  project_update: {
    subject: 'Project Update - {{project_name}} | {{company_name}}',
    body: `Dear {{client_name}},

We would like to provide you with an update on your project "{{project_name}}".

Project Status:
- Overall Progress: {{progress}}%
- Current Phase: {{current_phase}}
- Expected Completion: {{expected_completion}}

Recent Accomplishments:
{{recent_updates}}

Please feel free to reach out if you have any questions or would like to schedule a site visit.

Best regards,
{{sender_name}}
{{company_name}}`
  }
};

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('payment_request');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/email-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      // If no templates exist, use defaults
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const getTemplateForType = (type) => {
    const saved = templates.find(t => t.type === type);
    if (saved) return saved;
    // Return default template
    return {
      type,
      subject: DEFAULT_TEMPLATES[type]?.subject || '',
      body: DEFAULT_TEMPLATES[type]?.body || '',
      is_default: true
    };
  };

  const handleEdit = (type) => {
    const template = getTemplateForType(type);
    setCurrentTemplate({
      ...template,
      type
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!currentTemplate) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/email-templates`, {
        type: currentTemplate.type,
        subject: currentTemplate.subject,
        body: currentTemplate.body
      });
      toast.success('Email template saved successfully');
      fetchTemplates();
      setEditDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (type) => {
    setCurrentTemplate(getTemplateForType(type));
    setPreviewDialogOpen(true);
  };

  const handleReset = async (type) => {
    try {
      await axios.delete(`${API}/email-templates/${type}`);
      toast.success('Template reset to default');
      fetchTemplates();
    } catch (error) {
      // Template doesn't exist, already default
      toast.info('Template is already at default');
    }
  };

  // Replace template variables with sample data for preview
  const getPreviewContent = (template) => {
    const sampleData = {
      '{{client_name}}': 'John Smith',
      '{{vendor_name}}': 'ABC Suppliers',
      '{{project_name}}': 'Modern Villa - Powai',
      '{{company_name}}': 'Bella Casa Interiors',
      '{{amount}}': '2,50,000',
      '{{total_amount}}': '25,00,000',
      '{{payment_stage}}': 'Stage 2 - Design Approval',
      '{{bank_details}}': 'HDFC Bank\nA/C: 1234567890\nIFSC: HDFC0001234',
      '{{sender_name}}': user.name || 'Admin',
      '{{company_phone}}': '+91 98765 43210',
      '{{company_email}}': 'info@bellacasa.in',
      '{{invoice_number}}': 'INV-2024-001',
      '{{due_date}}': '15 Mar 2024',
      '{{validity_date}}': '30 Mar 2024',
      '{{payment_mode}}': 'Bank Transfer',
      '{{reference_id}}': 'PAY-2024-0123',
      '{{progress}}': '65',
      '{{current_phase}}': 'Interior Finishing',
      '{{expected_completion}}': '30 Apr 2024',
      '{{recent_updates}}': '- Flooring completed\n- Electrical work 90% done\n- Kitchen cabinet installation started'
    };

    let content = template?.body || '';
    Object.entries(sampleData).forEach(([key, value]) => {
      content = content.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return content;
  };

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-manrope text-2xl font-bold text-stone-900">Email Templates</h1>
            <p className="text-stone-600 mt-1">Customize email templates for client and vendor communications</p>
          </div>
        </div>

        <Tabs value={activeType} onValueChange={setActiveType} className="space-y-6">
          <TabsList className="grid grid-cols-5 w-full">
            {TEMPLATE_TYPES.map(type => (
              <TabsTrigger key={type.value} value={type.value} className="flex items-center gap-2">
                <type.icon size={16} />
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {TEMPLATE_TYPES.map(type => (
            <TabsContent key={type.value} value={type.value}>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${type.color}`}>
                        <type.icon size={20} className="text-white" />
                      </div>
                      <div>
                        <CardTitle>{type.label} Template</CardTitle>
                        <CardDescription>{type.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handlePreview(type.value)}>
                        <Eye size={14} className="mr-2" />
                        Preview
                      </Button>
                      <Button size="sm" onClick={() => handleEdit(type.value)}>
                        <Edit size={14} className="mr-2" />
                        Edit Template
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-stone-500">Subject Line</Label>
                      <p className="text-sm font-medium text-stone-700 mt-1 p-2 bg-stone-50 rounded">
                        {getTemplateForType(type.value).subject}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Email Body (Preview)</Label>
                      <div className="text-sm text-stone-600 mt-1 p-3 bg-stone-50 rounded whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {getTemplateForType(type.value).body?.substring(0, 300)}...
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-stone-500">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                        {`{{variable}}`} placeholders will be replaced automatically
                      </span>
                      {!getTemplateForType(type.value).is_default && (
                        <Button variant="ghost" size="sm" className="text-red-600 h-6" onClick={() => handleReset(type.value)}>
                          Reset to Default
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Edit Template Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail size={20} />
                Edit {TEMPLATE_TYPES.find(t => t.value === currentTemplate?.type)?.label} Template
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Subject Line</Label>
                <Input
                  value={currentTemplate?.subject || ''}
                  onChange={(e) => setCurrentTemplate(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject with {{variables}}"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email Body</Label>
                <Textarea
                  value={currentTemplate?.body || ''}
                  onChange={(e) => setCurrentTemplate(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Email content with {{variables}}"
                  className="mt-1 min-h-[400px] font-mono text-sm"
                />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {['{{client_name}}', '{{project_name}}', '{{amount}}', '{{company_name}}', '{{sender_name}}', '{{payment_stage}}', '{{bank_details}}', '{{due_date}}'].map(v => (
                    <code key={v} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{v}</code>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save size={14} className="mr-2" />
                {saving ? 'Saving...' : 'Save Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye size={20} />
                Email Preview
              </DialogTitle>
            </DialogHeader>
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-stone-100 p-3 border-b">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-stone-600">Subject:</span>
                  <span className="text-stone-900">{currentTemplate?.subject?.replace(/\{\{.*?\}\}/g, match => {
                    const sampleData = {
                      '{{project_name}}': 'Modern Villa - Powai',
                      '{{company_name}}': 'Bella Casa Interiors',
                      '{{invoice_number}}': 'INV-2024-001'
                    };
                    return sampleData[match] || match;
                  })}</span>
                </div>
              </div>
              <div className="p-4 bg-white whitespace-pre-wrap text-sm text-stone-700">
                {getPreviewContent(currentTemplate)}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
